import { isOpenRentStatus } from "./rent";
import { supabase } from "./supabase";
import { operatorStamped } from "@/lib/operator-stamped";

export type RentPaymentRow = {
  id: string;
  user_id: string | null;
  booking_id: number | null;
  type: string;
  amount_cents: number;
  /** COL-417: cents already received against this row. Absent on older reads. */
  received_cents?: number | null;
  currency: string;
  status: string;
  due_date: string | null;
  paid_at: string | null;
  stripe_payment_intent_id: string | null;
  stripe_charge_id: string | null;
  description: string | null;
  [key: string]: unknown;
};

/**
 * Month key `YYYY-MM` from an ISO date string (or date-only `YYYY-MM-DD`).
 * Returns `null` if the input is missing/invalid.
 */
export function monthKeyFromDue(
  dueIso: string | null | undefined
): string | null {
  if (!dueIso) return null;
  const s = String(dueIso).trim();
  if (s.length < 7) return null;
  return s.slice(0, 7);
}

/** Start of the next calendar month given `YYYY-MM`. Returns `YYYY-MM-01`. */
export function nextMonthStart(monthKey: string): string {
  const [y, m] = monthKey.split("-").map(Number);
  if (!y || !m) return monthKey + "-01";
  const ny = m === 12 ? y + 1 : y;
  const nm = m === 12 ? 1 : m + 1;
  return `${ny}-${String(nm).padStart(2, "0")}-01`;
}

/**
 * Upsert a rent payment row for (booking_id, type='rent', month-of-due_date).
 *
 * Guarantees at most ONE rent row per booking per calendar month — matching the
 * DB unique partial index on (booking_id, date_trunc('month', due_date)) WHERE type='rent'.
 *
 * Resolution rules:
 *  - If a row already exists for the month AND it is paid → return it as-is (never overwrite paid).
 *  - If a row exists in pending/scheduled/overdue → update fields we were asked to set
 *    (amount, due_date within same month, description) and return it.
 *  - Otherwise insert a new row.
 *
 * Call this instead of `supabase.from('payments').insert(...)` for any rent row
 * created from the client (admin Create Payment, tenant Stripe card flow, tenant bank transfer, etc.).
 */
export async function ensureRentPaymentForMonth(args: {
  userId: string;
  bookingId: number;
  dueDate: string; // YYYY-MM-DD
  amountCents: number;
  currency?: string;
  description?: string | null;
  status?: "pending" | "scheduled";
}): Promise<{
  payment: RentPaymentRow | null;
  created: boolean;
  error: Error | null;
}> {
  const {
    userId,
    bookingId,
    dueDate,
    amountCents,
    currency = "eur",
    description = null,
    status = "pending",
  } = args;

  const monthKey = monthKeyFromDue(dueDate);
  if (!monthKey) {
    return {
      payment: null,
      created: false,
      error: new Error("Invalid dueDate for rent payment upsert"),
    };
  }
  const monthStart = `${monthKey}-01`;
  const nextStart = nextMonthStart(monthKey);

  // Look for any existing rent row in the same month for this booking.
  // Prefer 'paid' first so early-paid rows aren't clobbered by a pending duplicate check.
  const { data: existingRows, error: selectError } = await supabase
    .from("payments")
    .select("*")
    .eq("booking_id", bookingId)
    .eq("type", "rent")
    .gte("due_date", monthStart)
    .lt("due_date", nextStart)
    .order("status", { ascending: false }); // 'paid' > 'pending' alphabetically (covers common case)

  if (selectError) {
    return {
      payment: null,
      created: false,
      error: selectError as unknown as Error,
    };
  }

  const existing = (existingRows ?? []) as RentPaymentRow[];
  const paidRow = existing.find((r) => r.status === "paid");
  if (paidRow) {
    // Already settled for this month — never overwrite.
    return { payment: paidRow, created: false, error: null };
  }

  // COL-417: `partially_paid` is an OPEN row — it is the live row for its
  // month and must be reused like any other. Left out of this list it matched
  // neither branch, so a part-paid month fell through to the INSERT below and
  // was saved only by the 23505 race handler catching its own unique-index
  // violation. `failed` is not in OPEN_RENT_STATUSES (it is a bounced
  // collection rather than a state the tenant is dunned in) but is reusable
  // here for the same reason: the money is still owed on that same row.
  const mutable = existing.find(
    (r) => isOpenRentStatus(String(r.status)) || String(r.status) === "failed"
  );
  if (mutable) {
    const patch: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
    };
    // COL-417: a row with money recorded against it is never repriced here.
    //
    // Callers pass what they believe the month costs, and since the reminders
    // surfaces now chase the REMAINING balance, that figure is the open amount
    // on a part-paid month — writing it to `amount_cents` would restate a EUR
    // 650 month as a EUR 350 one, turn EUR 300 received into full settlement,
    // and lose the shortfall from every total that reads the ledger. Changing
    // what a month costs is what the rent-schedule Edit action is for; this
    // helper exists to find or create the row, and repricing is a convenience
    // it only safely has while nothing has been received.
    const hasReceipts = Number(mutable.received_cents ?? 0) > 0;
    if (
      Number.isFinite(amountCents) &&
      amountCents > 0 &&
      mutable.amount_cents !== amountCents &&
      !hasReceipts
    ) {
      patch.amount_cents = amountCents;
    }
    if (mutable.due_date !== dueDate) patch.due_date = dueDate;
    if (description && mutable.description !== description)
      patch.description = description;
    // COL-417 review fix: `partially_paid` is not downgraded here, for the same
    // reason `overdue` beside it is not.
    //
    // `status` defaults to "pending" and every caller passes "pending"
    // explicitly, so without this guard merely *ensuring* a row — opening
    // "Pay now" — silently strips `partially_paid` with no money having moved.
    // That is not cosmetic: `AUTO_CHARGEABLE_STATUSES`
    // (supabase/functions/_shared/rent-charge.ts) is
    // ["pending","scheduled","overdue"] and excludes `partially_paid` on
    // purpose, because the unattended tick debits the full `amount_cents`
    // while only the on-demand path nets the remainder. A tenant who part-pays
    // EUR 300 of EUR 650, opens the checkout and abandons it would otherwise
    // be SEPA-debited the full EUR 650 on the next tick.
    //
    // Before `partially_paid` joined `mutable` above, such a row missed this
    // branch entirely and the 23505 handler returned it unpatched — that
    // accident is what kept this safe, so the guard has to be explicit now.
    //
    // Keyed on the STATUS, not on `hasReceipts`: a `failed` row that also has
    // receipts must still reopen to `pending`, because
    // `confirm-payment-from-session` refuses an explicit `failed` target and
    // leaves it out of its candidate lookup — a card retry on a row stuck at
    // `failed` would charge successfully and then fail to attach. Only
    // `partially_paid` is auto-chargeable-adjacent, so only it needs holding.
    if (
      status &&
      mutable.status !== status &&
      mutable.status !== "overdue" &&
      mutable.status !== "partially_paid"
    )
      patch.status = status;

    // Only write if there's something to change.
    if (Object.keys(patch).length > 1) {
      const { data: updated, error: updateError } = await supabase
        .from("payments")
        .update(patch)
        .eq("id", mutable.id)
        .select()
        .single();
      if (updateError) {
        return {
          payment: mutable,
          created: false,
          error: updateError as unknown as Error,
        };
      }
      return {
        payment: (updated as RentPaymentRow) ?? mutable,
        created: false,
        error: null,
      };
    }
    return { payment: mutable, created: false, error: null };
  }

  const { data: created, error: insertError } = await supabase
    .from("payments")
    .insert(
      operatorStamped<"payments">({
        user_id: userId,
        booking_id: bookingId,
        type: "rent",
        amount_cents: amountCents,
        currency,
        status,
        due_date: dueDate,
        description,
      })
    )
    .select()
    .single();

  if (insertError) {
    // A concurrent caller won the race and the DB unique index
    // (one rent row per booking per due-month) rejected this insert.
    // Re-select the winning row for the month and return it instead of erroring.
    if ((insertError as any)?.code === "23505") {
      const { data: raced } = await supabase
        .from("payments")
        .select("*")
        .eq("booking_id", bookingId)
        .eq("type", "rent")
        .gte("due_date", monthStart)
        .lt("due_date", nextStart)
        .order("status", { ascending: false })
        .limit(1);
      const racedRow = (raced && (raced[0] as RentPaymentRow)) || null;
      if (racedRow) {
        return { payment: racedRow, created: false, error: null };
      }
    }
    return {
      payment: null,
      created: false,
      error: insertError as unknown as Error,
    };
  }
  return { payment: created as RentPaymentRow, created: true, error: null };
}
